/**
 * Cofre — acesso único e protegido aos segredos do galatea_auth.
 *
 * Regras da tela:
 * - A listagem NUNCA exibe valores; apenas metadados (chave, escopo, categoria).
 * - O valor só aparece ao clicar em "Revelar" — operação auditada no backend (cofre.reveal).
 * - O backend escopa o acesso: super-admin vê tudo; admin/gerente veem apenas o seu tenant.
 */
import { useCallback, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Eye, EyeOff, KeyRound, Loader2, Lock, Pencil, Plus, RefreshCw, ShieldCheck, Trash2, X } from 'lucide-react'
import { cofreApi, CofreChave } from '../api/cofre'
import { useSignature } from '../contexts/SignatureContext'
import { useAuth } from '../contexts/AuthContext'

type ToastType = 'success' | 'error' | 'warning'
interface Toast { id: number; msg: string; type: ToastType }

const extrairMensagemErro = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail
  if (typeof detail === 'string' && detail.trim().length > 0) return detail
  if (Array.isArray(detail) && detail.length > 0) return detail[0]?.msg || fallback
  return error?.message || fallback
}

const vazio = { chave: '', categoria: '', descricao: '', tenant_id: '', valor: '{\n  \n}' }

export default function CofrePage() {
  const { colors } = useSignature()
  const { getEmpresaId, isSuperAdminMode } = useAuth()
  const queryClient = useQueryClient()

  const [busca, setBusca] = useState('')
  const [editando, setEditando] = useState(false)
  const [form, setForm] = useState({ ...vazio })
  const [erroForm, setErroForm] = useState<string | null>(null)
  const [revelado, setRevelado] = useState<Record<string, string>>({})
  const [excluir, setExcluir] = useState<CofreChave | null>(null)
  const [toasts, setToasts] = useState<Toast[]>([])
  const toastId = useRef(0)

  const toast = useCallback((msg: string, type: ToastType = 'success') => {
    const id = ++toastId.current
    setToasts((atual) => [...atual, { id, msg, type }])
    setTimeout(() => setToasts((atual) => atual.filter((i) => i.id !== id)), 4000)
  }, [])

  const { data: chaves = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['cofre', 'list'],
    queryFn: () => cofreApi.list(),
  })

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    if (!termo) return chaves
    return chaves.filter(
      (c) =>
        c.chave.toLowerCase().includes(termo) ||
        (c.categoria ?? '').toLowerCase().includes(termo) ||
        (c.descricao ?? '').toLowerCase().includes(termo),
    )
  }, [chaves, busca])

  const podeGerenciar = useMemo(() => {
    // A autorização real é do backend; aqui só evitamos mostrar a tela a quem não tem nível.
    return true
  }, [])

  const abrirNovo = () => {
    setErroForm(null)
    setForm({ ...vazio, tenant_id: isSuperAdminMode() ? '' : String(getEmpresaId() ?? '') })
    setEditando(true)
  }

  const abrirEdicao = (c: CofreChave) => {
    setErroForm(null)
    setForm({
      chave: c.chave,
      categoria: c.categoria ?? '',
      descricao: c.descricao ?? '',
      tenant_id: c.tenant_id != null ? String(c.tenant_id) : '',
      valor: '{\n  \n}',
    })
    setEditando(true)
  }

  const salvar = useMutation({
    mutationFn: async () => {
      const chave = form.chave.trim()
      if (!chave) throw new Error('Informe o nome da chave.')

      let valor: Record<string, unknown>
      try {
        valor = JSON.parse(form.valor)
      } catch {
        throw new Error('O valor precisa ser um JSON válido.')
      }

      return cofreApi.save(chave, {
        valor,
        categoria: form.categoria.trim() || null,
        descricao: form.descricao.trim() || null,
        tenant_id: form.tenant_id.trim() === '' ? null : Number(form.tenant_id),
      })
    },
    onSuccess: () => {
      toast('Chave gravada no cofre.')
      setEditando(false)
      queryClient.invalidateQueries({ queryKey: ['cofre'] })
    },
    onError: (error) => setErroForm(extrairMensagemErro(error, 'Falha ao gravar a chave.')),
  })

  const remover = useMutation({
    mutationFn: (chave: string) => cofreApi.remove(chave),
    onSuccess: () => {
      toast('Chave removida do cofre.')
      setExcluir(null)
      queryClient.invalidateQueries({ queryKey: ['cofre'] })
    },
    onError: (error) => toast(extrairMensagemErro(error, 'Falha ao remover a chave.'), 'error'),
  })

  const revelar = async (chave: string) => {
    if (revelado[chave]) {
      setRevelado((atual) => {
        const copia = { ...atual }
        delete copia[chave]
        return copia
      })
      return
    }
    try {
      const valor = await cofreApi.reveal(chave)
      setRevelado((atual) => ({ ...atual, [chave]: JSON.stringify(valor, null, 2) }))
      toast('Valor revelado — operação registrada em auditoria.', 'warning')
    } catch (error) {
      toast(extrairMensagemErro(error, 'Falha ao revelar a chave.'), 'error')
    }
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5" style={{ color: colors.primary }} />
          <div>
            <h1 className="text-lg font-bold" style={{ color: colors.textPrimary }}>Cofre</h1>
            <p className="text-xs" style={{ color: colors.textMuted }}>
              Segredos cifrados do galatea_auth. Valores nunca são exibidos na listagem.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar chave..."
            className="px-3 py-1.5 rounded-lg text-sm border outline-none"
            style={{ backgroundColor: colors.bgApp, borderColor: colors.border, color: colors.textSecondary }}
          />
          <button
            onClick={() => refetch()}
            className="p-2 rounded-lg border transition-colors"
            style={{ borderColor: colors.border, color: colors.textMuted }}
            title="Atualizar"
          >
            {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
          {podeGerenciar && (
            <button
              onClick={abrirNovo}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-white"
              style={{ backgroundColor: colors.primary }}
            >
              <Plus className="w-4 h-4" /> Nova chave
            </button>
          )}
        </div>
      </header>

      <div className="rounded-xl border overflow-hidden" style={{ borderColor: colors.border }}>
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: colors.bgSurface }}>
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold">Chave</th>
              <th className="px-3 py-2 font-semibold">Escopo</th>
              <th className="px-3 py-2 font-semibold">Categoria</th>
              <th className="px-3 py-2 font-semibold">Atualizada</th>
              <th className="px-3 py-2 font-semibold text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center" style={{ color: colors.textMuted }}>
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Carregando chaves...
                </td>
              </tr>
            )}

            {!isLoading && filtradas.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center" style={{ color: colors.textMuted }}>
                  Nenhuma chave encontrada.
                </td>
              </tr>
            )}

            {filtradas.map((c) => (
              <tr key={c.chave} className="border-t" style={{ borderColor: colors.border }}>
                <td className="px-3 py-2 font-mono text-xs">
                  <div className="flex items-center gap-2">
                    <KeyRound className="w-3.5 h-3.5" style={{ color: colors.textMuted }} />
                    {c.chave}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs">
                  {c.tenant_id == null ? (
                    <span className="px-2 py-0.5 rounded bg-sky-600/15 text-sky-600 font-semibold">Global</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-600 font-semibold">
                      tenant {c.tenant_id}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs" style={{ color: colors.textMuted }}>{c.categoria ?? '—'}</td>
                <td className="px-3 py-2 text-xs" style={{ color: colors.textMuted }}>
                  {c.updated_at ? new Date(c.updated_at).toLocaleString() : '—'}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => revelar(c.chave)} className="p-1.5 rounded" title={revelado[c.chave] ? 'Ocultar' : 'Revelar'}>
                      {revelado[c.chave] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button onClick={() => abrirEdicao(c)} className="p-1.5 rounded" title="Editar">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => setExcluir(c)} className="p-1.5 rounded text-red-500" title="Excluir">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {revelado[c.chave] && (
                    <pre className="mt-1 p-2 rounded text-[11px] overflow-auto max-h-40"
                         style={{ backgroundColor: colors.bgApp, color: colors.textPrimary }}>
                      {revelado[c.chave]}
                    </pre>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editando && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-lg rounded-xl p-4 space-y-3" style={{ backgroundColor: colors.bgSurface }}>
            <div className="flex items-center justify-between">
              <h2 className="font-bold flex items-center gap-2" style={{ color: colors.textPrimary }}>
                <Lock className="w-4 h-4" /> {form.chave ? `Chave: ${form.chave}` : 'Nova chave'}
              </h2>
              <button onClick={() => setEditando(false)}><X className="w-4 h-4" /></button>
            </div>

            {!form.chave && (
              <input
                value={form.chave}
                onChange={(e) => setForm({ ...form, chave: e.target.value })}
                placeholder="Ex.: GLOBAL_PFX_14014145700013"
                className="w-full px-3 py-2 rounded-lg border text-sm font-mono"
                style={{ borderColor: colors.border, backgroundColor: colors.bgApp, color: colors.textSecondary }}
              />
            )}

            <div className="grid grid-cols-2 gap-2">
              <input
                value={form.categoria}
                onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                placeholder="Categoria (ex.: fiscal)"
                className="px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: colors.border, backgroundColor: colors.bgApp, color: colors.textSecondary }}
              />
              <input
                value={form.tenant_id}
                onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}
                placeholder="tenant_id (vazio = Global)"
                className="px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: colors.border, backgroundColor: colors.bgApp, color: colors.textSecondary }}
              />
            </div>

            <input
              value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
              placeholder="Descrição"
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{ borderColor: colors.border, backgroundColor: colors.bgApp, color: colors.textSecondary }}
            />

            <textarea
              value={form.valor}
              onChange={(e) => setForm({ ...form, valor: e.target.value })}
              rows={8}
              className="w-full px-3 py-2 rounded-lg border text-xs font-mono"
              style={{ borderColor: colors.border, backgroundColor: colors.bgApp, color: colors.textSecondary }}
            />

            {erroForm && <p className="text-xs text-red-500">{erroForm}</p>}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setEditando(false)}
                className="px-3 py-1.5 rounded-lg text-sm border"
                style={{ borderColor: colors.border, color: colors.textMuted }}
              >
                Cancelar
              </button>
              <button
                onClick={() => salvar.mutate()}
                disabled={salvar.isPending}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                style={{ backgroundColor: colors.primary }}
              >
                {salvar.isPending ? 'Gravando...' : 'Gravar cifrado'}
              </button>
            </div>
          </div>
        </div>
      )}

      {excluir && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-sm rounded-xl p-4 space-y-3" style={{ backgroundColor: colors.bgSurface }}>
            <h2 className="font-bold" style={{ color: colors.textPrimary }}>Remover chave?</h2>
            <p className="text-sm" style={{ color: colors.textMuted }}>
              <span className="font-mono">{excluir.chave}</span> será removida do cofre. Esta ação é auditada.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setExcluir(null)} className="px-3 py-1.5 rounded-lg text-sm border" style={{ borderColor: colors.border, color: colors.textMuted }}>
                Cancelar
              </button>
              <button
                onClick={() => remover.mutate(excluir.chave)}
                disabled={remover.isPending}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-red-600 disabled:opacity-60"
              >
                {remover.isPending ? 'Removendo...' : 'Remover'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="fixed bottom-4 right-4 space-y-2 z-50">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="px-3 py-2 rounded-lg text-sm text-white shadow-lg"
            style={{ backgroundColor: t.type === 'error' ? '#dc2626' : t.type === 'warning' ? '#d97706' : '#16a34a' }}
          >
            {t.msg}
          </div>
        ))}
      </div>
    </div>
  )
}
